/**
 * CMRP Print List — prints only #filtered-products via a body-level print portal.
 */
(function () {
  const PRINT_BODY_CLASS = 'cmrp-print-active';
  const PRINT_PORTAL_ID = 'cmrp-print-portal';
  const PRODUCTS_ROOT = '#filtered-products';
  /** @type {Array<{ type: string, el: Element, component?: Element, prev?: Record<string, string>, parent?: Node | null, next?: Node | null }>} */
  let savedState = [];
  let printDigitalNoticeEnabled = false;

  function getProductsRoot() {
    return document.querySelector(PRODUCTS_ROOT);
  }

  function expandMaterialDetails(materialItem, viewMoreBtn) {
    const button =
      viewMoreBtn ||
      materialItem.querySelector('.view-more-btn, .view-more-btn-choice');
    if (!button || button.classList.contains('expanded')) return;

    let detailsSection = materialItem.querySelector('.material-details-expanded');
    if (!detailsSection) {
      detailsSection = document.createElement('div');
      detailsSection.className = 'material-details-expanded';
      const parent =
        materialItem.querySelector('.material-details, .material-text') ||
        materialItem;
      parent.appendChild(detailsSection);
    }

    if (typeof window.populateExpandedDetails === 'function') {
      window.populateExpandedDetails(materialItem, detailsSection);
    }

    button.classList.add('expanded');
    detailsSection.classList.add('expanded');
    savedState.push({ type: 'details', el: button });
  }

  function showPurchaseOptions(materialItem) {
    const purchaseOptions = materialItem.querySelector('.material-purchase-options');
    if (!purchaseOptions || purchaseOptions.classList.contains('show')) return;

    purchaseOptions.classList.add('show');
    savedState.push({ type: 'purchase-options', el: purchaseOptions });
  }

  function showAllFormatCollapses(materialItem) {
    materialItem.querySelectorAll('.format-collapse').forEach((collapse) => {
      if (collapse.classList.contains('cmrp-print-force-visible')) return;

      savedState.push({
        type: 'format-collapse',
        el: collapse,
        prev: {
          display: collapse.style.display,
          className: collapse.className,
        },
      });
      collapse.classList.add('cmrp-print-force-visible');
      collapse.style.display = 'block';
    });
  }

  function showChoicePanels() {
    const root = getProductsRoot();
    if (!root) return;

    root.querySelectorAll('.material-item.choice-info').forEach((panel) => {
      revealChoiceInfoPanel(panel);
    });
  }

  function revealChoiceInfoPanel(panel) {
    if (!panel || panel.classList.contains('choice-show')) return;

    savedState.push({
      type: 'choice-panel',
      el: panel,
      prev: {
        className: panel.className,
        display: panel.style.display,
      },
    });
    panel.classList.remove('choice-hide');
    panel.classList.add('choice-show', 'cmrp-print-force-visible');
    panel.style.display = 'block';
  }

  function prepareChoiceItemDomForPrint(choiceItem) {
    if (!choiceItem) return;

    const info = choiceItem.querySelector('.choice-info');
    if (!info) return;

    revealChoiceInfoPanel(info);

    const viewMoreBtn = choiceItem.querySelector(
      '.view-more-btn-choice, .view-more-btn'
    );
    if (viewMoreBtn && !viewMoreBtn.classList.contains('expanded')) {
      expandMaterialDetails(choiceItem, viewMoreBtn);
    }

    const purchaseOptions = info.querySelector('.material-purchase-options');
    if (purchaseOptions && !purchaseOptions.classList.contains('show')) {
      purchaseOptions.classList.add('show');
      savedState.push({ type: 'purchase-options', el: purchaseOptions });
    }

    info.querySelectorAll('.format-collapse').forEach((collapse) => {
      if (collapse.classList.contains('cmrp-print-force-visible')) return;

      savedState.push({
        type: 'format-collapse',
        el: collapse,
        prev: {
          display: collapse.style.display,
          className: collapse.className,
        },
      });
      collapse.classList.add('cmrp-print-force-visible');
      collapse.style.display = 'block';
    });
  }

  function getUsedPrintNoticeText() {
    if (window.CMRP_USED_PRINT_VARIANT_NOTICE) {
      return String(window.CMRP_USED_PRINT_VARIANT_NOTICE).trim();
    }
    const fromInput = document.getElementById('used_tooltip_message')?.value?.trim();
    if (fromInput) return fromInput;
    return 'Used materials: might not include supplemental materials like access cards or CDs.';
  }

  function getDigitalFeeNoticeText() {
    const fromConfig = window.digital_product_config?.notice_text?.trim();
    if (fromConfig) return fromConfig;
    const fromInput = document.getElementById('digital_fee_notice_text')?.value?.trim();
    if (fromInput) return fromInput;
    const amount =
      document.querySelector('.digital-fee-amount')?.textContent?.trim() ||
      window.digital_product_config?.price ||
      '5.99';
    return `To support the delivery of digital content to you, a non-refundable digital delivery fee will be applied to each digital material. The digital delivery fee per unit is $${amount}.`;
  }

  function isUsedPrintVariantLabel(text) {
    const t = String(text || '').toLowerCase();
    return (
      t.includes('buy used') ||
      t.includes('rent used') ||
      (t.includes('used') && (t.includes('buy') || t.includes('rent')))
    );
  }

  function rootHasUsedPrintVariants(root) {
    for (const row of root.querySelectorAll('.purchase-type')) {
      const label =
        row.querySelector(
          '.purchase-type__label, .purchase-type-label, .purchase-type__name'
        )?.textContent || '';
      if (isUsedPrintVariantLabel(label)) return true;
    }

    for (const el of root.querySelectorAll('.cmrp-suggested-print-variant-label')) {
      if (isUsedPrintVariantLabel(el.textContent)) return true;
    }

    return false;
  }

  function rootHasDigitalVariantsInResults(root) {
    if (root.querySelector('input.purchase-checkbox[data-is-digital="true"]')) {
      return true;
    }
    return (
      root.querySelector(
        '.cmrp-suggested-print-format[data-format="digital"] .cmrp-suggested-print-variant'
      ) != null
    );
  }

  function buildPrintFooterNoticesHtml(messages) {
    return messages
      .map(
        (message) =>
          `<p class="cmrp-print-inline-footer__notice">${escapeHtml(message)}</p>`
      )
      .join('');
  }

  function collectPrintFooterNotices(root) {
    const messages = [];

    if (rootHasUsedPrintVariants(root)) {
      messages.push(getUsedPrintNoticeText());
    }

    if (rootHasDigitalVariantsInResults(root) && printDigitalNoticeEnabled) {
      messages.push(getDigitalFeeNoticeText());
    }

    return messages;
  }

  async function getDigitalNoticeEnabled() {
    if (typeof window.isDigitalDeliveryNoticeStoreEnabled !== 'function') {
      return false;
    }
    try {
      return await window.isDigitalDeliveryNoticeStoreEnabled();
    } catch {
      return false;
    }
  }

  function expandCourseSectionNotesForPrint(root) {
    root
      .querySelectorAll(
        '.course-section-note, .course-section-note--section, .course-section-note--course'
      )
      .forEach((noteEl) => {
        const textEl = noteEl.querySelector('.course-section-note__text');
        const toggleWrap = noteEl.querySelector('.course-section-note__toggle');
        const toggle = noteEl.querySelector('.course-section-note__toggle-link');
        if (!textEl) return;

        const fullNote = (
          noteEl.dataset.fullNote ||
          noteEl.getAttribute('data-full-note') ||
          ''
        ).trim();

        if (fullNote) {
          if (textEl.textContent === fullNote && !toggle) return;

          savedState.push({
            type: 'course-note',
            el: noteEl,
            mode: 'text',
            prevText: textEl.textContent,
            prevToggleDisplay: toggleWrap?.style.display || '',
          });
          textEl.textContent = fullNote;
          if (toggleWrap) toggleWrap.style.display = 'none';
          return;
        }

        if (!toggle) return;

        const wasExpanded = toggle.getAttribute('aria-expanded') === 'true';
        if (!wasExpanded) {
          toggle.click();
        }

        savedState.push({
          type: 'course-note',
          el: noteEl,
          mode: 'click',
          toggle,
          wasExpanded,
        });
      });
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function decodeHtmlEntities(str) {
    if (!str) return '';
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function formatPrice(price) {
    const num = parseFloat(price);
    if (Number.isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num);
  }

  function extractVariantId(gid) {
    if (!gid) return '';
    const s = String(gid);
    const parts = s.split('/');
    return parts[parts.length - 1] || s;
  }

  function parseJsonArray(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  function parseJsonObject(raw) {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  }

  function getProductsFromComponent(component) {
    const prop = component.productsData;
    if (prop) {
      const fromProp =
        typeof prop === 'string' ? parseJsonArray(prop) : Array.isArray(prop) ? prop : null;
      if (fromProp?.length) return fromProp;
    }

    const attrCandidates = [
      component.dataset?.cmrpProducts,
      component.getAttribute('products-data'),
      component.getAttribute('productsdata'),
      component.getAttribute('productsData'),
    ];

    for (const raw of attrCandidates) {
      const parsed = parseJsonArray(raw);
      if (parsed?.length) return parsed;
    }

    return null;
  }

  function getSuggestedProductsFromComponent(component) {
    const fromComponent = getProductsFromComponent(component);
    if (fromComponent?.length) return mergeProductsByISBN(fromComponent);

    const sectionRaw =
      component.sectionData ||
      component.getAttribute('section-data') ||
      component.getAttribute('sectiondata');
    let sectionId = '';
    if (sectionRaw) {
      try {
        const sectionData =
          typeof sectionRaw === 'string' ? JSON.parse(sectionRaw) : sectionRaw;
        sectionId = String(sectionData?.courseInfo?.selectedsectionId || '');
      } catch {
        /* ignore */
      }
    }

    const pool = window.courseData?.products_Materials;
    if (Array.isArray(pool) && pool.length) {
      const suggested = pool.filter((p) => {
        if (p?.material_type !== 'Suggested') return false;
        if (!sectionId) return true;
        const sectionIds = Array.isArray(p.sectionId) ? p.sectionId : [p.sectionId];
        return sectionIds.includes(sectionId);
      });
      if (suggested.length) return mergeProductsByISBN(suggested);
    }

    return null;
  }

  function isUnifiedViewEnabled() {
    return (
      typeof window !== 'undefined' &&
      window.shopifySettings &&
      window.shopifySettings.unifiedViewCMRP === true
    );
  }

  function parsePrintRelationshipISBN(printRelationshipISBN) {
    if (!printRelationshipISBN) return [];

    if (Array.isArray(printRelationshipISBN)) {
      return printRelationshipISBN;
    }

    if (typeof printRelationshipISBN === 'string') {
      try {
        const parsed = JSON.parse(printRelationshipISBN);
        return Array.isArray(parsed) ? parsed : [printRelationshipISBN];
      } catch {
        return [printRelationshipISBN];
      }
    }

    return [];
  }

  function mergeProductsByISBN(products) {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return products || [];
    }

    if (!isUnifiedViewEnabled()) {
      return products;
    }

    const mergedProducts = [];
    const productsToRemove = new Set();
    const isbnToProductMap = new Map();

    products.forEach((product, index) => {
      const isbn =
        product.metafields?.isbn_ean ||
        product.metafields?.course_materials?.isbn_ean;
      if (isbn) {
        if (!isbnToProductMap.has(isbn)) {
          isbnToProductMap.set(isbn, []);
        }
        isbnToProductMap.get(isbn).push({ product, index });
      }
    });

    products.forEach((product, index) => {
      if (productsToRemove.has(index)) return;

      const printRelationshipISBN =
        product.metafields?.print_relationship_isbn ||
        product.metafields?.course_materials?.print_relationship_isbn;
      const bookType =
        product.metafields?.book_type_ind ||
        product.metafields?.course_materials?.book_type_ind;

      if (!printRelationshipISBN || printRelationshipISBN === '') return;
      if (bookType !== 'CEB' || !printRelationshipISBN) return;

      const relatedISBNs = parsePrintRelationshipISBN(printRelationshipISBN);

      for (const isbn of relatedISBNs) {
        const matchingProducts = isbnToProductMap.get(isbn) || [];
        const printProduct = matchingProducts.find(({ product: p }) => {
          const pBookType =
            p.metafields?.book_type_ind ||
            p.metafields?.course_materials?.book_type_ind;
          return pBookType === 'TXT' && p.material_type === product.material_type;
        });

        if (!printProduct) continue;

        const digitalVariants = product.variants || [];
        const printVariants = printProduct.product.variants || [];
        const seenVariantIds = new Set();
        const uniquePrintVariants = printVariants.filter((variant) => {
          const variantId = extractVariantId(variant.id);
          if (seenVariantIds.has(variantId)) return false;
          seenVariantIds.add(variantId);
          return true;
        });
        const uniqueDigitalVariants = digitalVariants.filter((variant) => {
          const variantId = extractVariantId(variant.id);
          if (seenVariantIds.has(variantId)) return false;
          seenVariantIds.add(variantId);
          return true;
        });

        printProduct.product.variants = [
          ...uniquePrintVariants,
          ...uniqueDigitalVariants,
        ];

        if (!printProduct.product.metafields) {
          printProduct.product.metafields = {};
        }
        if (!printProduct.product.metafields.custom) {
          printProduct.product.metafields.custom = {};
        }
        if (!printProduct.product.metafields.custom.variant_digital_status) {
          printProduct.product.metafields.custom.variant_digital_status = {};
        }

        digitalVariants.forEach((variant) => {
          const variantId = extractVariantId(variant.id);
          if (!variantId) return;
          const digitalProductStatus =
            product.metafields?.custom?.variant_digital_status?.[variantId];
          printProduct.product.metafields.custom.variant_digital_status[variantId] =
            digitalProductStatus !== undefined ? digitalProductStatus : 'true';
        });

        printVariants.forEach((variant) => {
          const variantId = extractVariantId(variant.id);
          if (!variantId) return;
          if (
            variantId in printProduct.product.metafields.custom.variant_digital_status
          ) {
            return;
          }
          const existingStatus =
            printProduct.product.metafields?.custom?.variant_digital_status?.[
              variantId
            ];
          if (existingStatus === undefined) {
            printProduct.product.metafields.custom.variant_digital_status[variantId] =
              false;
          }
        });

        productsToRemove.add(index);
        break;
      }
    });

    products.forEach((product, index) => {
      if (!productsToRemove.has(index)) {
        mergedProducts.push(product);
      }
    });

    return mergedProducts;
  }

  function isVariantPurchasable(variant) {
    return (
      Boolean(variant?.available) ||
      String(variant?.inventory_policy || '').toLowerCase() === 'continue'
    );
  }

  function dedupeVariantsById(variants) {
    const seen = new Set();
    return variants.filter((variant) => {
      const rawId = variant?.id;
      if (!rawId) return false;
      if (seen.has(rawId)) return false;
      seen.add(rawId);
      return true;
    });
  }

  function filterVariantsByDigitalStatus(variants, variantDigitalStatus) {
    if (!variants || !Array.isArray(variants)) return [];
    if (!variantDigitalStatus || typeof variantDigitalStatus !== 'object') {
      return variants;
    }
    return variants.filter((variant) => {
      const variantId = extractVariantId(variant.id);
      return Object.prototype.hasOwnProperty.call(variantDigitalStatus, variantId);
    });
  }

  /** Mirrors bundle-products variantUtils: infer format when status map omits merged digital SKUs. */
  function inferVariantFormatFromMetafields(variant) {
    let isDigital = false;
    let isPrint = false;
    const metafields = Array.isArray(variant?.metafields) ? variant.metafields : [];

    metafields.forEach((mf) => {
      if (!mf || typeof mf !== 'object') return;
      if (mf.namespace === 'cm_digital') {
        if (mf.is_digital === true || mf.is_digital === 'true') {
          isDigital = true;
        }
      }
      if (mf.namespace === 'course_materials') {
        if (mf.book_type_ind === 'CEB') isDigital = true;
        else if (mf.book_type_ind === 'TXT') isPrint = true;
      }
    });

    return { isDigital, isPrint };
  }

  /**
   * Variants that CMRP actually surfaces (matches React variantUtils + html-renderer OOS rule).
   */
  function getPrintVisibleVariants(product) {
    const variants = dedupeVariantsById(
      Array.isArray(product?.variants) ? product.variants : []
    );
    const variantDigitalStatus = product?.metafields?.custom?.variant_digital_status;

    let visible;
    if (!variantDigitalStatus || typeof variantDigitalStatus !== 'object') {
      visible = variants;
    } else {
      const filtered = filterVariantsByDigitalStatus(variants, variantDigitalStatus);
      const seenIds = new Set(
        filtered.map((v) => extractVariantId(v.id)).filter(Boolean)
      );
      const inferredExtras = variants.filter((variant) => {
        const id = extractVariantId(variant.id);
        if (!id || seenIds.has(id)) return false;
        const { isDigital, isPrint } = inferVariantFormatFromMetafields(variant);
        return isDigital || isPrint;
      });
      visible = [...filtered, ...inferredExtras];
    }

    if (window.hideOutOfStockVariants === true) {
      visible = visible.filter(isVariantPurchasable);
    }

    return visible;
  }

  function getVariantIsDigital(variant, variantDigitalStatus) {
    const id = extractVariantId(variant.id);
    const status = variantDigitalStatus?.[id];
    if (status === true || status === 'true') return true;
    if (status === false || status === 'false') return false;

    const mfs = Array.isArray(variant?.metafields) ? variant.metafields : [];
    const hit = mfs.find((m) => m && m.is_digital != null);
    if (hit) {
      const value = hit.is_digital;
      if (value === true || String(value).toLowerCase() === 'true') return true;
    }

    const cmDigital = variant?.metafields?.cm_digital?.is_digital;
    if (cmDigital != null) {
      if (cmDigital === true || String(cmDigital).toLowerCase() === 'true') {
        return true;
      }
    }

    return false;
  }

  function groupSuggestedVariants(variants, variantDigitalStatus) {
    const digital = [];
    const print = [];
    variants.forEach((variant) => {
      if (getVariantIsDigital(variant, variantDigitalStatus)) {
        digital.push(variant);
      } else {
        print.push(variant);
      }
    });
    return { digital, print };
  }

  function getSuggestedVariantLabel(variant) {
    const title = (variant.title || variant.option2 || '').toUpperCase();
    if (title.includes('RENT')) return title.includes('USED') ? 'Rent Used' : 'Rent';
    if (title.includes('USED') || title.includes('BUY USED')) return 'Buy Used';
    if (title.includes('NEW') || title.includes('BUY NEW')) return 'Buy New';
    return variant.option2 || variant.option1 || 'Buy';
  }

  function getSuggestedSubscriptionDays(variant) {
    const mfs = Array.isArray(variant?.metafields) ? variant.metafields : [];
    const row = mfs.find((mf) => mf.namespace === 'cm_digital' && mf.subscription);
    const subscription = row?.subscription;
    if (!subscription || String(subscription).trim() === '0') return '';
    const days = parseInt(subscription, 10);
    if (Number.isNaN(days)) return '';
    return `Rental duration: ${days} ${days === 1 ? 'day' : 'days'}`;
  }

  function getSuggestedProductField(product, field) {
    const cm = product?.metafields?.course_materials || {};
    const root = product?.metafields || {};
    const custom = product?.metafields?.custom || {};
    const value = cm[field] ?? root[field] ?? custom[field];
    return value != null && String(value).trim() !== '' ? String(value).trim() : '';
  }

  function getSuggestedAuthor(product) {
    const Handler = window.ProductSystem?.SOLIDProductHandler;
    if (Handler) {
      try {
        const handler = new Handler();
        const name = handler.extractAuthorName?.(product);
        if (name && String(name).trim()) return String(name).trim();
      } catch {
        /* fall through */
      }
    }
    return (
      getSuggestedProductField(product, 'author_name') ||
      getSuggestedProductField(product, 'author')
    );
  }

  function getExpandedDetailsFromProduct(product) {
    const Handler = window.ProductSystem?.SOLIDProductHandler;
    if (!Handler) return null;
    try {
      const handler = new Handler();
      return {
        publisher: handler.extractPublisher?.(product) || '',
        format: handler.extractFormat?.(product) || '',
        copyright: handler.extractCopyRight?.(product) || '',
        publication: handler.extractPublication?.(product) || '',
      };
    } catch {
      return null;
    }
  }

  function buildSuggestedMetaRow(label, value) {
    if (!value) return '';
    return `<div class="cmrp-suggested-print-meta-row"><span class="cmrp-suggested-print-meta-label">${escapeHtml(label)}:</span> <span class="cmrp-suggested-print-meta-value">${escapeHtml(value)}</span></div>`;
  }

  function buildSuggestedVariantHtml(variant) {
    const label = getSuggestedVariantLabel(variant);
    const price = formatPrice(variant.price);
    const rental = getSuggestedSubscriptionDays(variant);
    const rentalHtml = rental
      ? `<div class="cmrp-suggested-print-variant-rental">${escapeHtml(rental)}</div>`
      : '';

    return `
      <div class="cmrp-suggested-print-variant">
        <div class="cmrp-suggested-print-variant-header">
          <span class="cmrp-suggested-print-variant-label">${escapeHtml(label)}</span>
          <span class="cmrp-suggested-print-variant-price">${escapeHtml(price)}</span>
        </div>
        ${rentalHtml}
      </div>
    `;
  }

  function formatDisplayLabel(formatKey) {
    const key = String(formatKey || '').toLowerCase();
    if (key === 'print') return 'Physical';
    if (!key) return '';
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  function buildSuggestedFormatHtml(formatKey, variants) {
    if (!variants.length) return '';
    const variantHtml = variants.map(buildSuggestedVariantHtml).join('');
    const dataFormat = String(formatKey || '').toLowerCase();
    return `
      <div class="cmrp-suggested-print-format" data-format="${escapeHtml(dataFormat)}">
        <div class="cmrp-suggested-print-format-label">${escapeHtml(formatDisplayLabel(formatKey))}</div>
        <div class="cmrp-suggested-print-variant-list">${variantHtml}</div>
      </div>
    `;
  }

  function buildPrintProductRow(product, options = {}) {
    const { badge = '', subtitle = '', itemClass = '' } = options;
    const title = decodeHtmlEntities(product.title) || 'Product';
    const author = getSuggestedAuthor(product);
    const edition = getSuggestedProductField(product, 'edition');
    const isbn =
      getSuggestedProductField(product, 'isbn_ean') ||
      getSuggestedProductField(product, 'isbn');
    const expandedDetails = getExpandedDetailsFromProduct(product);
    const publisher =
      expandedDetails?.publisher || getSuggestedProductField(product, 'publisher');
    const format = expandedDetails?.format || getSuggestedProductField(product, 'format');
    const copyright =
      expandedDetails?.copyright || getSuggestedProductField(product, 'copyright');
    const publication =
      expandedDetails?.publication || getSuggestedProductField(product, 'publication_date');

    const variantDigitalStatus = product?.metafields?.custom?.variant_digital_status || {};
    const availableVariants = getPrintVisibleVariants(product);
    const { digital, print } = groupSuggestedVariants(
      availableVariants,
      variantDigitalStatus
    );

    const formatsHtml =
      buildSuggestedFormatHtml('Digital', digital) +
      buildSuggestedFormatHtml('Print', print);

    const variantsInner =
      formatsHtml ||
      (availableVariants.length
        ? `<div class="cmrp-suggested-print-variant-list">${availableVariants
            .map(buildSuggestedVariantHtml)
            .join('')}</div>`
        : '');

    const variantsColumn = variantsInner
      ? `<div class="cmrp-suggested-print-variants">${variantsInner}</div>`
      : '';

    const badgeHtml = badge
      ? `<div class="cmrp-suggested-print-badge">${escapeHtml(badge)}</div>`
      : '';
    const subtitleHtml = subtitle
      ? `<div class="cmrp-suggested-print-subtitle">${escapeHtml(subtitle)}</div>`
      : '';

    const itemClasses = ['cmrp-suggested-print-item'];
    if (itemClass) itemClasses.push(itemClass);

    return `
      <div class="${itemClasses.join(' ')}">
        <div class="cmrp-suggested-print-details">
          ${badgeHtml}
          ${subtitleHtml}
          <h3 class="cmrp-suggested-print-title">${escapeHtml(title)}</h3>
          ${author ? `<div class="cmrp-suggested-print-author">${escapeHtml(author)}</div>` : ''}
          <div class="cmrp-suggested-print-meta">
            ${buildSuggestedMetaRow('Edition', edition)}
            ${buildSuggestedMetaRow('ISBN', isbn)}
            ${buildSuggestedMetaRow('Publisher', publisher)}
            ${buildSuggestedMetaRow('Format', format)}
            ${buildSuggestedMetaRow('Copyright', copyright)}
            ${buildSuggestedMetaRow('Publication', publication)}
          </div>
        </div>
        ${variantsColumn}
      </div>
    `;
  }

  function buildSuggestedProductHtml(product) {
    return buildPrintProductRow(product, { badge: 'SUGGESTED' });
  }

  const CHOICE_MATERIAL_TYPES = [
    'Choice',
    'Choice/Required',
    'Choice/Recommended',
  ];

  function isChoiceMaterialType(materialType) {
    return CHOICE_MATERIAL_TYPES.includes(materialType);
  }

  function getChoiceSectionId(section) {
    const choiceItems = section.querySelector('[id^="choice-items-"]');
    if (choiceItems?.id) {
      return choiceItems.id.replace(/^choice-items-/, '');
    }

    const finalItems = section.querySelector('[id^="choice-final-items-"]');
    if (finalItems?.id) {
      return finalItems.id.replace(/^choice-final-items-/, '');
    }

    const minimumInput = section.querySelector('[id^="choice-minimum-"]');
    if (minimumInput?.id) {
      return minimumInput.id.replace(/^choice-minimum-/, '');
    }

    return '';
  }

  function getOriginalProductsFromComponent(component) {
    const attrCandidates = [
      component.dataset?.cmrpProducts,
      component.getAttribute('products-data'),
      component.getAttribute('productsdata'),
      component.getAttribute('productsData'),
    ];

    for (const raw of attrCandidates) {
      const parsed = parseJsonArray(raw);
      if (parsed?.length) return parsed;
    }

    return null;
  }

  function extractChoiceProductIdFromElement(el) {
    if (!el) return '';

    const fromData = el.dataset?.productId?.trim();
    if (fromData) return fromData;

    const fromPlus = el.querySelector('[data-product-id]')?.dataset?.productId?.trim();
    if (fromPlus) return fromPlus;

    const plusId = el.querySelector('[id^="plus-box-product-"]')?.id || '';
    const plusMatch = plusId.match(/^plus-box-product-(.+?)-[^-]+$/);
    if (plusMatch?.[1]) return plusMatch[1];

    const infoId = el.querySelector('[id^="choice-info-product-"]')?.id || '';
    const infoMatch = infoId.match(/^choice-info-product-(.+)$/);
    if (infoMatch?.[1]) return infoMatch[1];

    return '';
  }

  function findProductInPool(pool, productId) {
    if (!productId || !Array.isArray(pool)) return null;
    const id = String(productId);
    return (
      pool.find((product) => String(product?.id) === id) ||
      pool.find((product) => extractVariantId(product?.id) === extractVariantId(id)) ||
      null
    );
  }

  function getChoiceProductsFromGrid(section) {
    const choiceItemsRoot = section.querySelector('[id^="choice-items-"]');
    if (!choiceItemsRoot) return null;

    const pool = window.courseData?.products_Materials;
    if (!Array.isArray(pool) || !pool.length) return null;

    const seen = new Set();
    const products = [];

    choiceItemsRoot.querySelectorAll('.choice-item').forEach((item) => {
      const productId = extractChoiceProductIdFromElement(item);
      if (!productId || seen.has(productId)) return;

      const product = findProductInPool(pool, productId);
      if (!product || !isChoiceMaterialType(product.material_type)) return;

      seen.add(productId);
      products.push(product);
    });

    return products.length ? products : null;
  }

  function getChoiceTotalFromSection(section, products) {
    const hintText =
      section.querySelector('.choice-products-header__hint')?.textContent?.trim() || '';
    const match = hintText.match(/out of\s+(\d+)/i);
    if (match?.[1]) return match[1];

    const gridCount = section.querySelectorAll('[id^="choice-items-"] .choice-item').length;
    if (gridCount > 0) return String(gridCount);

    return String(products.length);
  }

  function getChoiceProductsFromSection(section) {
    const component = section.querySelector('choice-products-component');
    let rawProducts = null;

    if (component) {
      rawProducts = getOriginalProductsFromComponent(component);
    }

    if (!rawProducts?.length) {
      const sectionId = getChoiceSectionId(section);
      const pool = window.courseData?.products_Materials;
      if (Array.isArray(pool) && pool.length) {
        rawProducts = pool.filter((product) => {
          if (!isChoiceMaterialType(product?.material_type)) return false;
          if (!sectionId) return true;
          const sectionIds = Array.isArray(product.sectionId)
            ? product.sectionId
            : [product.sectionId];
          return sectionIds.includes(sectionId);
        });
      }
    }

    if (!rawProducts?.length) return null;

    const merged = mergeProductsByISBN(rawProducts);
    const gridProducts = getChoiceProductsFromGrid(section);
    if (!gridProducts?.length) return merged;

    const printable = gridProducts.map((gridProduct) => {
      const byId = merged.find(
        (product) => String(product?.id) === String(gridProduct?.id)
      );
      if (byId) return byId;

      const gridIsbn =
        gridProduct?.metafields?.isbn_ean ||
        gridProduct?.metafields?.course_materials?.isbn_ean;
      if (gridIsbn) {
        const byIsbn = merged.find((product) => {
          const isbn =
            product?.metafields?.isbn_ean ||
            product?.metafields?.course_materials?.isbn_ean;
          return isbn && String(isbn) === String(gridIsbn);
        });
        if (byIsbn) return byIsbn;
      }

      return mergeProductsByISBN([gridProduct, ...rawProducts])[0] || gridProduct;
    });

    return printable.length ? printable : merged;
  }

  function getChoiceMinimum(section) {
    const input = section.querySelector('[id^="choice-minimum-"]');
    const value = input?.value?.trim();
    return value || '';
  }

  function extractPurchaseTypesFromContainer(container) {
    const variants = [];
    const hideOos = window.hideOutOfStockVariants === true;

    container.querySelectorAll('.purchase-type').forEach((row) => {
      if (hideOos && row.classList.contains('unavailable')) return;
      if (row.style.display === 'none') return;

      const label =
        row
          .querySelector(
            '.purchase-type-label, .purchase-type__label, .purchase-type__name'
          )
          ?.textContent?.trim() || '';
      const price = row.querySelector('.purchase-price')?.textContent?.trim() || '';
      const rental =
        row.querySelector('.rental-duration')?.textContent?.trim() || '';

      if (!label) return;
      variants.push({ label, price, rental });
    });

    return variants;
  }

  function extractChoiceProductPrintData(choiceItem) {
    const card = choiceItem.querySelector('.choice-product-card');
    const info = choiceItem.querySelector('.choice-info');
    if (!card) return null;

    const title =
      decodeHtmlEntities(
        card.querySelector('.choice-product-card__title, .material-title')
          ?.textContent
      ) || '';
    const author = card.querySelector('.material-author')?.textContent?.trim() || '';

    const meta = {};
    card.querySelectorAll('.choice-meta-row').forEach((row) => {
      const label = row
        .querySelector('.choice-meta-label')
        ?.textContent?.replace(/:$/, '')
        .trim();
      const value = row.querySelector('.choice-meta-value')?.textContent?.trim();
      if (label && value) meta[label] = value;
    });

    info?.querySelectorAll('.detail-row').forEach((row) => {
      const valueEl = row.querySelector('.detail-value');
      const value = valueEl?.textContent?.trim();
      if (!value) return;

      let label = row.querySelector('.detail-label')?.textContent?.replace(/:$/, '').trim();
      if (!label) {
        const raw = row.textContent?.trim() || '';
        label = raw.replace(value, '').replace(/:$/, '').trim();
      }
      if (label && !meta[label]) meta[label] = value;
    });

    const formats = [];
    const purchaseArea = info?.querySelector('.material-purchase-options');
    if (purchaseArea) {
      const collapses = purchaseArea.querySelectorAll('.format-collapse');
      if (collapses.length) {
        collapses.forEach((collapse) => {
          const formatKey = collapse.getAttribute('data-format') || '';
          const variants = extractPurchaseTypesFromContainer(collapse);
          if (variants.length) {
            formats.push({ format: formatKey, variants });
          }
        });
      } else {
        const variants = extractPurchaseTypesFromContainer(purchaseArea);
        if (variants.length) formats.push({ format: '', variants });
      }
    }

    if (!title && !formats.length) return null;

    return { title, author, meta, formats };
  }

  function buildChoiceVariantHtmlFromDom(variant) {
    const rentalHtml = variant.rental
      ? `<div class="cmrp-suggested-print-variant-rental">${escapeHtml(variant.rental)}</div>`
      : '';

    return `
      <div class="cmrp-suggested-print-variant">
        <div class="cmrp-suggested-print-variant-header">
          <span class="cmrp-suggested-print-variant-label">${escapeHtml(variant.label)}</span>
          <span class="cmrp-suggested-print-variant-price">${escapeHtml(variant.price)}</span>
        </div>
        ${rentalHtml}
      </div>
    `;
  }

  function buildChoiceProductHtmlFromDom(data) {
    const metaRows = Object.entries(data.meta || {})
      .map(([label, value]) => buildSuggestedMetaRow(label, value))
      .join('');

    const formatsHtml = (data.formats || [])
      .map(({ format, variants }) => {
        if (!variants?.length) return '';
        const variantHtml = variants.map(buildChoiceVariantHtmlFromDom).join('');
        if (!format) {
          return `<div class="cmrp-suggested-print-variant-list">${variantHtml}</div>`;
        }
        return `
          <div class="cmrp-suggested-print-format" data-format="${escapeHtml(String(format).toLowerCase())}">
            <div class="cmrp-suggested-print-format-label">${escapeHtml(formatDisplayLabel(format))}</div>
            <div class="cmrp-suggested-print-variant-list">${variantHtml}</div>
          </div>
        `;
      })
      .join('');

    return `
      <div class="cmrp-suggested-print-item">
        <div class="cmrp-suggested-print-details">
          <div class="cmrp-suggested-print-badge">CHOICE</div>
          <h3 class="cmrp-suggested-print-title">${escapeHtml(data.title)}</h3>
          ${data.author ? `<div class="cmrp-suggested-print-author">${escapeHtml(data.author)}</div>` : ''}
          ${metaRows ? `<div class="cmrp-suggested-print-meta">${metaRows}</div>` : ''}
        </div>
        ${
          formatsHtml
            ? `<div class="cmrp-suggested-print-variants">${formatsHtml}</div>`
            : ''
        }
      </div>
    `;
  }

  function getChoicePrintItemsFromSection(section) {
    const choiceItems = section.querySelectorAll('.choice-products-grid > .choice-item');
    const fromDom = [];

    choiceItems.forEach((choiceItem) => {
      prepareChoiceItemDomForPrint(choiceItem);
      const data = extractChoiceProductPrintData(choiceItem);
      if (data) fromDom.push(data);
    });

    if (fromDom.length) return { items: fromDom, source: 'dom' };

    const products = getChoiceProductsFromSection(section);
    if (!products?.length) return null;

    return {
      items: products,
      source: 'json',
    };
  }

  function buildChoiceProductHtml(product) {
    return buildPrintProductRow(product, { badge: 'CHOICE' });
  }

  function buildChoicePrintHtml(items, choiceMinimum, choiceTotal, source = 'dom') {
    const total = choiceTotal || String(items.length);
    const hint = choiceMinimum
      ? `Choose ${choiceMinimum} out of ${total} materials`
      : `Choose from ${total} materials`;
    const itemsHtml =
      source === 'json'
        ? items.map(buildChoiceProductHtml).join('')
        : items.map(buildChoiceProductHtmlFromDom).join('');

    return `
      <div class="cmrp-choice-print-header">
        <span class="cmrp-choice-print-badge">Required</span>
        <h3 class="cmrp-choice-print-header-title">Choice materials</h3>
        <p class="cmrp-choice-print-hint">*${escapeHtml(hint)}</p>
      </div>
      ${itemsHtml}
    `;
  }

  function prepareChoiceForPrint() {
    const root = getProductsRoot();
    if (!root) return;

    root
      .querySelectorAll('.choice-products-section, .choice-list')
      .forEach((section) => {
        if (
          section.nextElementSibling?.classList?.contains('cmrp-choice-print-wrap')
        ) {
          return;
        }

        const printData = getChoicePrintItemsFromSection(section);
        if (!printData?.items?.length) return;

        const choiceMinimum = getChoiceMinimum(section);
        const choiceTotal = getChoiceTotalFromSection(section, printData.items);
        const wrap = document.createElement('div');
        wrap.className = 'cmrp-choice-print-wrap';
        wrap.innerHTML = buildChoicePrintHtml(
          printData.items,
          choiceMinimum,
          choiceTotal,
          printData.source
        );

        section.insertAdjacentElement('afterend', wrap);
        section.classList.add('cmrp-choice-print-source-hidden');

        savedState.push({
          type: 'choice-print',
          el: wrap,
          section,
        });
      });
  }

  function getBundleParentProduct(item) {
    const fromDataset = parseJsonObject(item.dataset.cmrpBundleParent);
    if (fromDataset) return fromDataset;

    const titleEl = item.querySelector('.material-item-main .material-title');
    if (!titleEl) return null;

    const title = titleEl.textContent.trim();
    const pool = window.courseData?.products_Materials || [];
    return pool.find((p) => decodeHtmlEntities(p.title) === title) || null;
  }

  function getBundleChildProducts(item) {
    const fromDataset = parseJsonArray(item.dataset.cmrpBundleProducts);
    if (fromDataset?.length) return fromDataset;

    const component = item.querySelector('bundle-products-component');
    if (component) {
      const fromComponent = getProductsFromComponent(component);
      if (fromComponent?.length) return fromComponent;
    }

    const ids = (item.dataset.bundleChildIds || '').split(',').filter(Boolean);
    if (!ids.length) return null;

    const pool = window.courseData?.products_Materials || [];
    const matched = ids
      .map((id) => pool.find((p) => String(p.id) === String(id)))
      .filter(Boolean);
    return matched.length ? matched : null;
  }

  function buildBundlePrintHtml(parent, children) {
    const total = children.length;
    const parentRow = buildPrintProductRow(parent, {
      badge: 'BUNDLE',
      itemClass: 'cmrp-bundle-print-item--parent',
    });
    const childRows = children
      .map((child, index) =>
        buildPrintProductRow(child, {
          subtitle: `Bundle item - ${index + 1}/${total}`,
          itemClass: 'cmrp-bundle-print-item--child',
        })
      )
      .join('');

    return `<div class="cmrp-bundle-print-inner">${parentRow}${childRows}</div>`;
  }

  function prepareBundlesForPrint() {
    const root = getProductsRoot();
    if (!root) return;

    root.querySelectorAll('.material-item--bundle').forEach((item) => {
      if (item.nextElementSibling?.classList?.contains('cmrp-bundle-print-wrap')) {
        return;
      }

      const parent = getBundleParentProduct(item);
      if (!parent) return;

      const children = getBundleChildProducts(item) || [];
      const wrap = document.createElement('div');
      wrap.className = 'cmrp-bundle-print-wrap';
      wrap.innerHTML = buildBundlePrintHtml(parent, children);

      item.insertAdjacentElement('afterend', wrap);
      item.classList.add('cmrp-bundle-print-source-hidden');

      savedState.push({
        type: 'bundle-print',
        el: wrap,
        item,
      });
    });
  }

  function buildSuggestedPrintHtml(products) {
    const itemsHtml = products.map(buildSuggestedProductHtml).join('');
    return `
      <div class="cmrp-suggested-print-header">
        <h3 class="cmrp-suggested-print-header-title">Suggested by your Campus Store</h3>
      </div>
      ${itemsHtml}
    `;
  }

  function prepareSuggestedForPrint() {
    const root = getProductsRoot();
    if (!root) return;

    root.querySelectorAll('suggested-materials-component').forEach((component) => {
      if (
        component.nextElementSibling?.classList?.contains('cmrp-suggested-print-wrap')
      ) {
        return;
      }

      const products = getSuggestedProductsFromComponent(component);
      if (!products?.length) return;

      const wrap = document.createElement('div');
      wrap.className = 'cmrp-suggested-print-wrap';
      wrap.setAttribute('data-cmrp-suggested-for', component.id || '');
      wrap.innerHTML = buildSuggestedPrintHtml(products);

      component.insertAdjacentElement('afterend', wrap);
      component.classList.add('cmrp-suggested-print-source-hidden');

      savedState.push({
        type: 'suggested-print',
        el: wrap,
        component,
      });
    });
  }

  function getPrintCampusTermTitle() {
    const displayEl = document.getElementById('selected-campus-display');
    const fromDisplay = window.CourseMaterialsUtils?.getCampusTermHeadingText
      ? CourseMaterialsUtils.getCampusTermHeadingText(displayEl)
      : displayEl?.textContent?.trim();
    if (fromDisplay) return fromDisplay;

    const campusEntities = window.courseData?.ddcs_campus_entity;
    let campus = '';
    let termName = '';

    const firstFilter = window.courseMaterialsPage?.selectedFilters?.[0];
    if (firstFilter) {
      campus = firstFilter.selectedCampus || '';
      const termId = firstFilter.selectedTermId || '';
      termName =
        window.courseData?.ddcs_term?.find((t) => t.term_id === termId)?.name ||
        firstFilter.selectedTerm ||
        '';
    }

    if (!campus || !termName) {
      const queryCampusTerm =
        typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm
          : (key) => document.querySelector(`#campus-term-dropdowns #${key}`);

      const campusSelect = queryCampusTerm('campus');
      const termSelect = queryCampusTerm('term');

      if (!campus && campusSelect?.value) {
        campus = campusSelect.value;
      }
      if (!termName && termSelect?.value) {
        const termId = termSelect.value;
        termName =
          window.courseData?.ddcs_term?.find((t) => t.term_id === termId)?.name ||
          termSelect.options?.[termSelect.selectedIndex]?.text ||
          termId;
      }
    }

    if (!campus && !termName) return '';

    const Utils = window.CourseMaterialsUtils;
    if (Utils?.formatCampusTermTitle) {
      return Utils.formatCampusTermTitle(campus, termName, campusEntities);
    }

    if (Utils?.isSingleCampusStore?.(campusEntities)) {
      return termName || campus;
    }
    if (campus && termName) return `${campus} | ${termName}`;
    return termName || campus;
  }

  function injectPrintHeader() {
    const container = getProductsRoot();
    if (!container) return;

    // Multi-campus results already have per-group titles in the DOM.
    if (
      container.classList.contains('has-multi-campus-groups') ||
      container.querySelector('.campus-materials-group .campus-term-bar--inline')
    ) {
      return;
    }

    const title = getPrintCampusTermTitle();
    if (!title) return;

    const header = document.createElement('div');
    header.className = 'cmrp-print-header';
    header.innerHTML = `<h1 class="cmrp-print-header-title">${escapeHtml(title)}</h1>`;
    container.insertBefore(header, container.firstChild);
    savedState.push({ type: 'injected-header', el: header });
  }

  function injectPrintFooter() {
    const container = getProductsRoot();
    if (!container) return;

    const notices = collectPrintFooterNotices(container);
    if (!notices.length) return;

    const footer = document.createElement('div');
    footer.className = 'cmrp-print-inline-footer';
    footer.innerHTML = buildPrintFooterNoticesHtml(notices);
    container.appendChild(footer);
    savedState.push({ type: 'injected-footer', el: footer });
  }

  /**
   * Move #filtered-products to a top-level portal so print pagination only
   * counts that content (avoids blank pages from hidden layout siblings).
   */
  function mountPrintPortal() {
    const root = getProductsRoot();
    if (!root || root.closest(`#${PRINT_PORTAL_ID}`)) return;

    let portal = document.getElementById(PRINT_PORTAL_ID);
    if (!portal) {
      portal = document.createElement('div');
      portal.id = PRINT_PORTAL_ID;
      portal.className = 'cmrp-print-portal';
      document.body.appendChild(portal);
    }

    savedState.push({
      type: 'portal-mount',
      el: root,
      parent: root.parentNode,
      next: root.nextSibling,
    });

    portal.appendChild(root);
  }

  function unmountPrintPortal() {
    const entry = savedState.find((item) => item.type === 'portal-mount');
    if (!entry) return;

    const { el, parent, next } = entry;
    if (parent && parent.isConnected && el) {
      if (next && next.parentNode === parent) {
        parent.insertBefore(el, next);
      } else {
        parent.appendChild(el);
      }
    }

    document.getElementById(PRINT_PORTAL_ID)?.remove();
  }

  /** Hide OOS rows the renderer omitted when hideOutOfStockVariants is on. */
  function hideDomPurchaseTypesNotRendered(root) {
    if (window.hideOutOfStockVariants !== true) return;

    root.querySelectorAll('.purchase-type.unavailable').forEach((row) => {
      savedState.push({
        type: 'hidden-purchase-type',
        el: row,
        prev: { display: row.style.display },
      });
      row.style.display = 'none';
    });
  }

  async function prepareForPrint() {
    const root = getProductsRoot();
    if (!root) return;

    savedState = [];
    printDigitalNoticeEnabled = await getDigitalNoticeEnabled();

    root.querySelectorAll('.material-item').forEach((item) => {
      if (item.classList.contains('material-item--bundle')) return;
      if (item.classList.contains('choice-item')) return;

      expandMaterialDetails(item);
      showPurchaseOptions(item);
      showAllFormatCollapses(item);
    });

    showChoicePanels();
    injectPrintHeader();
    expandCourseSectionNotesForPrint(root);
    prepareBundlesForPrint();
    prepareChoiceForPrint();
    prepareSuggestedForPrint();
    hideDomPurchaseTypesNotRendered(root);
    injectPrintFooter();
    mountPrintPortal();
    document.body.classList.add(PRINT_BODY_CLASS);
  }

  function cleanupAfterPrint() {
    document.body.classList.remove(PRINT_BODY_CLASS);
    unmountPrintPortal();

    savedState.forEach((entry) => {
      const { el, type, prev } = entry;
      if (type === 'portal-mount') return;
      if (
        type === 'injected-header' ||
        type === 'injected-footer' ||
        type === 'suggested-print' ||
        type === 'bundle-print' ||
        type === 'choice-print'
      ) {
        el.remove();
        if (type === 'suggested-print' && entry.component) {
          entry.component.classList.remove('cmrp-suggested-print-source-hidden');
        }
        if (type === 'bundle-print' && entry.item) {
          entry.item.classList.remove('cmrp-bundle-print-source-hidden');
        }
        if (type === 'choice-print' && entry.section) {
          entry.section.classList.remove('cmrp-choice-print-source-hidden');
        }
      } else if (type === 'details') {
        el.classList.remove('expanded');
        const item = el.closest('.material-item');
        item?.querySelector('.material-details-expanded')?.classList.remove('expanded');
      } else if (type === 'purchase-options') {
        el.classList.remove('show');
      } else if (type === 'format-collapse' && prev) {
        el.className = prev.className;
        el.style.display = prev.display;
      } else if (type === 'choice-panel' && prev) {
        el.className = prev.className;
        el.style.display = prev.display;
      } else if (type === 'hidden-purchase-type' && prev) {
        el.style.display = prev.display;
      } else if (type === 'course-note') {
        if (entry.mode === 'text') {
          const textEl = el.querySelector('.course-section-note__text');
          const toggleWrap = el.querySelector('.course-section-note__toggle');
          if (textEl && entry.prevText != null) {
            textEl.textContent = entry.prevText;
          }
          if (toggleWrap && entry.prevToggleDisplay != null) {
            toggleWrap.style.display = entry.prevToggleDisplay;
          }
        } else if (
          entry.mode === 'click' &&
          entry.toggle &&
          !entry.wasExpanded &&
          entry.toggle.getAttribute('aria-expanded') === 'true'
        ) {
          entry.toggle.click();
        }
      }
    });

    savedState = [];
  }

  function handlePrintClick() {
    const root = getProductsRoot();
    if (!root) return;

    const hasContent =
      root.querySelector('.course-section, .adoption-status-section') != null;
    if (!hasContent) return;

    prepareForPrint().then(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  }

  function updatePrintButtonVisibility() {
    const btn = document.getElementById('cmrp-print-list-btn');
    const root = getProductsRoot();
    if (!btn || !root) return;

    const hasContent =
      root.querySelectorAll('.course-section, .adoption-status-section').length >
      0;
    btn.hidden = !hasContent;
  }

  function init() {
    const btn = document.getElementById('cmrp-print-list-btn');
    if (!btn) return;

    btn.addEventListener('click', handlePrintClick);
    window.addEventListener('afterprint', cleanupAfterPrint);

    document.addEventListener('materialsLoaded', () => {
      setTimeout(updatePrintButtonVisibility, 100);
    });

    updatePrintButtonVisibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
